import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import config from '../../../config';
import { getStoredUser, getSessionHeaders, readJsonResponse } from './tpmRequest';
import { TpmStatusBadge } from './TpmMaintenanceList';

function formatDate(value) {
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

function formatNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue.toLocaleString() : '';
}

function getRecord(body) {
    if (Array.isArray(body)) return body[0] || null;
    if (body && body.data && typeof body.data === 'object' && !Array.isArray(body.data)) return body.data;
    return body || null;
}

function InfoCell({ label, value }) {
    return (
        <Col sm={12} md={4} style={{ marginBottom: '8px' }}>
            <div style={{ border: '1px solid #d7dce5', padding: '8px 10px', minHeight: '44px' }}>
                <strong>{label}: </strong>{value === '' || value === null || value === undefined ? '-' : value}
            </div>
        </Col>
    );
}

function PhotoStrip({ label, photoUrls }) {
    const items = Array.isArray(photoUrls) ? photoUrls.filter(Boolean) : [];
    if (!items.length) return null;
    return (
        <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>{label}</div>
            <div className="d-flex flex-wrap gap-2 mt-1">
                {items.map((photoUrl, index) => (
                    <a key={`${photoUrl}-${index}`} href={photoUrl} target="_blank" rel="noopener noreferrer">
                        Photo {index + 1}
                    </a>
                ))}
            </div>
        </div>
    );
}

export default function TpmMaintenanceDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const requestSequence = useRef(0);

    const loadRecord = useCallback(() => {
        const user = getStoredUser();
        if (!user || !user.accountId) {
            setError('Please sign in to view TPM maintenance records.');
            setLoading(false);
            return Promise.resolve();
        }

        const requestId = ++requestSequence.current;
        setLoading(true);
        setError('');
        return fetch(`${config.apiUrl}/getOneTpmMaintenance?id=${encodeURIComponent(id)}`, {
            method: 'GET',
            headers: getSessionHeaders(user)
        })
            .then(readJsonResponse)
            .then(body => {
                if (requestId !== requestSequence.current) return;
                setRecord(getRecord(body));
                setLoading(false);
            })
            .catch(loadError => {
                if (requestId !== requestSequence.current) return;
                setError(loadError.message || 'Failed to load TPM maintenance record.');
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        loadRecord();
    }, [loadRecord]);

    const changeStatus = (nextStatus) => {
        const user = getStoredUser();
        if (!user || !user.accountId) {
            setError('Please sign in to update TPM maintenance records.');
            return;
        }

        // Warn but allow: the tool room legitimately skips steps sometimes, and a
        // hard gate on closing just invites ticking boxes that were not done.
        if (nextStatus === 'Closed') {
            const incomplete = Number(record && record.incompleteTaskCount) || 0;
            const message = incomplete > 0
                ? `${incomplete} task(s) are not marked complete. Close this maintenance anyway?`
                : 'Close this maintenance record?';
            if (!window.confirm(message)) return;
        } else if (!window.confirm('Reopen this maintenance record?')) {
            return;
        }

        setSaving(true);
        setError('');
        fetch(`${config.apiUrl}/updateTpmMaintenanceStatus`, {
            method: 'POST',
            headers: getSessionHeaders(user),
            body: JSON.stringify({ id, status: nextStatus, closedFrom: 'web' })
        })
            .then(readJsonResponse)
            .then(body => {
                setRecord(getRecord(body));
                setSaving(false);
            })
            .catch(saveError => {
                setError(saveError.message || 'Failed to update TPM maintenance status.');
                setSaving(false);
            });
    };

    if (loading) return <div className="container py-3">Loading TPM maintenance record...</div>;

    if (error && !record) {
        return (
            <div className="container py-3">
                <div className="alert alert-danger" role="alert">{error}</div>
                <Button variant="outline-secondary" onClick={() => navigate('/tooling/tpm')}>Back</Button>
            </div>
        );
    }

    const item = record || {};
    const tasks = Array.isArray(item.tasks) ? item.tasks : [];
    const isClosed = String(item.status) === 'Closed';

    return (
        <div className="container py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h1 className="h3 mb-0">TPM Maintenance — Tier {item.tier || '-'}</h1>
                <div className="d-flex gap-2 align-items-center">
                    <TpmStatusBadge status={item.status} />
                    <Button
                        variant={isClosed ? 'warning' : 'success'}
                        disabled={saving}
                        onClick={() => changeStatus(isClosed ? 'Opened' : 'Closed')}
                    >
                        {saving ? 'Saving...' : (isClosed ? 'Reopen' : 'Mark as Closed')}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => navigate('/tooling/tpm')}>Back</Button>
                </div>
            </div>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}

            <Row>
                <InfoCell label="Part No" value={item.partNo} />
                <InfoCell label="Tooling No" value={item.toolingNo} />
                <InfoCell label="Repair Ticket" value={item.repairTicketId} />
                <InfoCell label="Interval PCS" value={formatNumber(item.intervalPcs)} />
                <InfoCell label="Triggered At PCS" value={formatNumber(item.triggeredAtPcs)} />
                <InfoCell label="Triggered" value={formatDate(item.createdAt || item.created_at)} />
            </Row>

            {isClosed && (
                <Row>
                    <InfoCell label="Closed By" value={item.closedBy} />
                    <InfoCell label="Closed At" value={formatDate(item.closedAt)} />
                    <InfoCell label="Closed From" value={item.closedFrom} />
                </Row>
            )}

            <h5 style={{ marginTop: '14px' }}>Maintenance Tasks</h5>
            <div style={{ color: '#6c757d', marginBottom: '8px' }}>
                Task completion is recorded on the mobile app. This view is read-only apart from the status.
            </div>
            <table className="table table-bordered table-sm">
                <thead>
                    <tr>
                        <th style={{ width: '48px' }}>#</th>
                        <th>Task</th>
                        <th style={{ width: '90px' }}>Done</th>
                        <th>Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {tasks.map((task, index) => (
                        <tr key={task.seq || index}>
                            <td>{task.seq || index + 1}</td>
                            <td>
                                {task.description || <span style={{ color: '#adb5bd' }}>—</span>}
                                <PhotoStrip label="Reference photos" photoUrls={task.photoUrls} />
                                <PhotoStrip label="Completion photos" photoUrls={task.completionPhotoUrls} />
                            </td>
                            <td style={{ textAlign: 'center' }}>{task.completed ? 'Yes' : ''}</td>
                            <td>{task.remarks || ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
